import {
  CameraNotAccessibleError,
  isAuthFailureStatus,
} from 'src/core/video/cameraNotAccessibleError';

interface ParsedSDP {
  iceUfrag: string;
  icePwd: string;
  medias: string[];
}

export class WhepSignallingClosed extends Error {}

/**
 * Manages signalling back and forth between the UI and a WHEP server.
 * This is based heavily on https://github.com/bluenviron/mediamtx/blob/main/internal/core/webrtc_read_index.html.
 */
export class WhepSignalling {
  private whepURL: string;
  private queuedCandidates: RTCIceCandidate[] = [];
  private eTag: string | undefined;
  private sessionUrl: string | undefined;
  private offerData: ParsedSDP | undefined;
  private isClosed = false;

  /** This is the sessionId, supplied by MediaMTX. */
  private sessionId: string | undefined;

  constructor(whepURL: string) {
    this.whepURL = whepURL;
  }

  // Roughly the equivalent of `sendOffer` and `onRemoteAnswer` in the MediaMTX player.
  // https://github.com/bluenviron/mediamtx/blob/8aa26d6fb369bb922135639c3c278b8693f6bad0/internal/servers/webrtc/read_index.html#L265
  async sendOffer(
    offer: RTCSessionDescriptionInit,
  ): Promise<{ remoteDescription: RTCSessionDescription; sessionId: string | undefined }> {
    if (this.isClosed) {
      throw new WhepSignallingClosed('Signalling is closed');
    }
    if (!offer.sdp) {
      throw new Error('Offer is missing sdp');
    }
    this.offerData = parseOffer(offer.sdp);

    const res = await fetch(this.whepURL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/sdp',
      },
      body: offer.sdp,
    });

    // A 401/403 here is a per-camera WHEP access failure, not an app-session
    // problem. Throw CameraNotAccessibleError so the tile shows "not accessible"
    // (matching the HLS/playlist paths) rather than a generic error, and so the
    // whole client is never logged out. Any other non-201 stays a generic error.
    if (isAuthFailureStatus(res.status)) {
      throw new CameraNotAccessibleError(res.status);
    }
    if (res.status !== 201) {
      throw new Error(`sendOffer expected 201 but got ${res.status}`);
    }

    // Resolve the `Location` header to a fully qualified URL.
    const location = res.headers.get('Location');
    if (location) {
      const sessionUrl = new URL(location, this.whepURL);

      // Preserve JWT from the original WHEP URL if present
      const jwt = new URL(this.whepURL).searchParams.get('jwt');
      if (jwt) {
        sessionUrl.searchParams.set('jwt', jwt);
      }

      this.sessionUrl = sessionUrl.toString();
    }

    // The 'Id' header is the MediaMTX session ID. Note that in the MediaMTX
    // logs it only shows the first 8 bytes of this value. Also note that this
    // field is not part of the WHEP standard - it's proprietary to MediaMTX, so
    // don't freak out if it isn't there.
    this.sessionId = res.headers.get('Id') || undefined;

    // MediaMTX 1.1.0 and lower will give us 'E-Tag' instead of 'e-tag'.
    // Once all nodes are running 1.1.1 or higher, we can safely remove the
    // "E-Tag" version.
    this.eTag = res.headers.get('ETag') || res.headers.get('E-Tag') || undefined;
    if (!this.eTag) {
      throw new Error('Missing ETag');
    }

    if (this.queuedCandidates.length) {
      const queuedCandidates = this.queuedCandidates;
      this.queuedCandidates = [];
      await this.sendLocalCandidates(this.eTag, this.offerData, queuedCandidates);
    }

    const remoteSdp = await res.text();
    return {
      remoteDescription: new RTCSessionDescription({
        type: 'answer',
        sdp: remoteSdp,
      }),
      sessionId: this.sessionId,
    };
  }

  async sendLocalCandidate(iceCandidate: RTCIceCandidate | null): Promise<void> {
    if (iceCandidate && iceCandidate.candidate) {
      if (this.eTag && this.offerData) {
        await this.sendLocalCandidates(this.eTag, this.offerData, [iceCandidate]);
      } else {
        this.queuedCandidates.push(iceCandidate);
      }
    }
  }

  async sendLocalCandidates(
    eTag: string,
    offerData: ParsedSDP,
    iceCandidates: RTCIceCandidate[],
  ): Promise<void> {
    if (this.isClosed) {
      throw new WhepSignallingClosed('Signalling is closed');
    }
    const res = await fetch(this.sessionUrl ?? this.whepURL, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/trickle-ice-sdpfrag',
        'If-Match': eTag,
      },
      body: generateSdpFragment(offerData, iceCandidates),
    });

    switch (res.status) {
      case 204:
        // Success!
        break;
      case 404:
        throw new Error(`sendICECandidates: stream not found for ${this.sessionId}`);
      default:
        throw new Error(
          `sendICECandidates expected 204 but got ${res.status} for ${this.sessionId}`,
        );
    }
  }

  /**
   * Ask MediaMTX to drop the WHEP session. Resolves once the server has answered, so callers can
   * close the peer connection afterwards (see `closeWhepSession`).
   *
   * A 404 means MediaMTX had already dropped the session, which is the outcome we wanted.
   */
  async close(): Promise<void> {
    this.isClosed = true;
    if (this.sessionUrl) {
      try {
        await fetch(this.sessionUrl, {
          method: 'DELETE',
          credentials: 'include',
          keepalive: true,
        });
      } catch {
        // Best-effort teardown. The DELETE tells MediaMTX to drop the WHEP
        // session, but MediaMTX garbage-collects orphaned sessions anyway.
        // A network failure here is expected during unmount or navigation and
        // is not actionable. Swallow it so it does not become an unhandled
        // rejection. The `keepalive` flag lets the request complete during
        // page unload.
      }
    }
  }
}

/**
 * Longest we keep a peer connection open waiting for the WHEP DELETE to come back. The connection
 * is still receiving media during that wait, so a stalled request must not hold it open.
 */
export const WHEP_TEARDOWN_TIMEOUT_MS = 2_000;

/**
 * Tear down a WHEP session in the order MediaMTX expects: DELETE the session first, then close the
 * peer connection.
 *
 * Closing the peer connection first makes MediaMTX drop the session on its own, so the DELETE that
 * follows lands on a session that no longer exists and comes back 404 — harmless, but it looks like
 * a defect in the network log.
 *
 * @param signalling The session to delete.
 * @param pc The peer connection to close once the session is gone, or after
 *   `WHEP_TEARDOWN_TIMEOUT_MS` if the DELETE hasn't answered by then.
 * @returns Resolves once the peer connection has been closed.
 */
export function closeWhepSession(signalling: WhepSignalling, pc: RTCPeerConnection): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const closePeer = () => {
      if (done) return;
      done = true;
      clearTimeout(timeout);
      try {
        pc.close();
      } catch (err) {
        console.error('Error closing RTCPeerConnection: ', err);
      }
      resolve();
    };

    const timeout = setTimeout(closePeer, WHEP_TEARDOWN_TIMEOUT_MS);
    signalling.close().finally(closePeer);
  });
}

function parseOffer(offer: string): ParsedSDP {
  const ret: ParsedSDP = {
    iceUfrag: '',
    icePwd: '',
    medias: [],
  };

  for (const line of offer.split('\r\n')) {
    if (line.startsWith('m=')) {
      ret.medias.push(line.slice('m='.length));
    } else if (ret.iceUfrag === '' && line.startsWith('a=ice-ufrag:')) {
      ret.iceUfrag = line.slice('a=ice-ufrag:'.length);
    } else if (ret.icePwd === '' && line.startsWith('a=ice-pwd:')) {
      ret.icePwd = line.slice('a=ice-pwd:'.length);
    }
  }

  return ret;
}

function generateSdpFragment(offerData: ParsedSDP, candidates: RTCIceCandidate[]) {
  const candidatesByMedia: Record<number, RTCIceCandidate[]> = {};
  for (const candidate of candidates) {
    const mid = candidate.sdpMLineIndex || 0;
    if (candidatesByMedia[mid] === undefined) {
      candidatesByMedia[mid] = [];
    }
    candidatesByMedia[mid].push(candidate);
  }

  let frag =
    'a=ice-ufrag:' + offerData.iceUfrag + '\r\n' + 'a=ice-pwd:' + offerData.icePwd + '\r\n';

  let mid = 0;

  for (const media of offerData.medias) {
    if (candidatesByMedia[mid] !== undefined) {
      frag += 'm=' + media + '\r\n' + 'a=mid:' + mid + '\r\n';

      for (const candidate of candidatesByMedia[mid]) {
        frag += 'a=' + candidate.candidate + '\r\n';
      }
    }
    mid++;
  }

  return frag;
}
